import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useClassroom } from '../../classroom/api';
import { useTheme, useThemeSync } from '../../theme/api';
import { logoUrl } from '../../theme/applyTheme';
import { useSubjects, useWeek } from '../api';
import { slotAt } from '../now';
import { PlannerSheet } from '../sheet/PlannerSheet';
import { SheetTitle } from '../sheet/SheetTitle';
import { useNow } from '../useBoard';
import { usePrintOptions } from '../usePrintOptions';
import { currentWeek, sameWeek } from '../weekMath';
import type { IsoWeek } from '../weekMath';
import { BoardControls } from './BoardControls';

type BoardLayoutProps = {
  target: IsoWeek;
  /** One day only (the day board). */
  day?: number;
  backHref: string;
  previousHref?: string;
  nextHref?: string;
  /** Rendered above the grid, inside the sheet (the day board puts the date here). */
  children?: ReactNode;
};

/** Full-screen, read-only sheet for the smartboard: the same sheet as on the laptop, larger, today and now marked. */
export function BoardLayout({ target, day, backHref, previousHref, nextHref, children }: BoardLayoutProps) {
  useThemeSync();
  // The board has no toolbar, but Ctrl+P on it must still follow the remembered print style.
  usePrintOptions();
  const week = useWeek(target.year, target.week);
  const subjects = useSubjects();
  const classroom = useClassroom().data;
  const theme = useTheme().data;
  const now = useNow();
  const slot = sameWeek(target, currentWeek()) ? slotAt(week.data?.periods ?? [], now) : { weekday: null, periodId: null };
  // The notes row only earns its place on the board when there is something to read.
  const dayNotes = week.data?.dayNotes?.length ? week.data.dayNotes : undefined;

  return (
    // Padding on a wrapper, not margins on <main>: margins would collapse through #root and make the page taller than the screen.
    // A fixed height (not min-height) so the lesson rows can share it; a long day plan still scrolls instead of being cut off.
    <div className="board flex h-screen flex-col p-3">
      <BoardControls backHref={backHref} {...(previousHref ? { previousHref } : {})} {...(nextHref ? { nextHref } : {})} />
      <main className="flex min-h-0 flex-1 flex-col">
        {week.isPending || subjects.isPending ? (
          <p>Laden…</p>
        ) : week.isError || subjects.isError ? (
          <p role="alert">De week kon niet worden geladen. Vernieuw de pagina.</p>
        ) : week.data === null ? (
          <p className="text-[clamp(1rem,2vw,2rem)]">
            Deze week bestaat nog niet.{' '}
            <Link to={`/week/${target.year}/${target.week}`} className="underline">
              Maak hem eerst aan
            </Link>
            .
          </p>
        ) : (
          <PlannerSheet
            // The day view has no break rows, as on the laptop.
            periods={day ? week.data.periods.filter((period) => !period.isBreak) : week.data.periods}
            subjects={subjects.data}
            lessons={week.data.lessons}
            mode="board"
            week={target}
            {...(day ? { day } : {})}
            highlight={slot}
            {...(dayNotes ? { dayNotes } : {})}
            title={<SheetTitle logoSrc={theme ? logoUrl(theme) : null} name={classroom?.name ?? ''} />}
          >
            {children}
          </PlannerSheet>
        )}
      </main>
    </div>
  );
}
